import { Maybe, maybe } from '@passionware/monads';
import { PopoverContentProps } from '@radix-ui/react-popover';
import {
  addMonths,
  differenceInCalendarMonths,
  endOfDay,
  isSameDay,
  set,
  startOfDay,
  startOfMonth
} from 'date-fns';
import {
  ArrowRight,
  Calendar as CalendarIcon,
  CornerDownLeft,
  Info,
  X
} from 'lucide-react';
import {
  ComponentPropsWithRef,
  createContext,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { DateRange } from 'react-day-picker';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { Overwrite } from 'v5/platform/typescript';
import { DateInput, DateInputProps } from './_common/DateInput';
import { Calendar, CalendarProps } from './Calendar';
import {
  InputGroup,
  InputGroupButton,
  InputGroupSize,
  InputGroupVariant
} from './InputGroup';
import { Kbd } from './Kbd';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from './Popover';
import { Switch } from './Switch';
import { SimpleTooltip } from './Tooltip';

export type DatePickerProps = Overwrite<
  ComponentPropsWithRef<'div'>,
  {
    variant?: InputGroupVariant;
    size?: InputGroupSize;
    leftSlot?: ReactNode;
    placeholder?: string;
    granularity?: DateInputProps['granularity'];
    allowChangeGranularity?: boolean;
    onIncludeHoursChange?: (include: boolean) => void;
    onBlur?: (e: FocusEvent) => void;
    onFocus?: (e: FocusEvent) => void;
    disabled?: boolean;
    readOnly?: boolean;
    popoverExtra?: ReactNode;
    align?: PopoverContentProps['align'];
    side?: PopoverContentProps['side'];
    minDate?: Date;
    maxDate?: Date;
    allowedRanges?: DatePickerAllowedRange[];
    modifiers?: CalendarProps['modifiers'];
    clearable?: boolean;
  } & CommonContextProps
>;

export interface DatePickerAllowedRange {
  from: Date;
  to: Date;
}

export type DatePickerChangeReason =
  | 'input'
  | 'clear'
  | 'close'
  | 'enter'
  | 'preset';

export type CommonContextProps =
  | {
      mode: 'range';
      value: Maybe<DateRange>;
      onChange: (
        value: Maybe<DateRange>,
        reason: DatePickerChangeReason
      ) => void;
    }
  | {
      mode: 'single';
      value: Maybe<Date>;
      onChange: (value: Maybe<Date>, reason: DatePickerChangeReason) => void;
    };

export type DatePickerContext = CommonContextProps & {
  onOpenChange: (open: boolean) => void;
};
const datePickerContext = createContext<Maybe<DatePickerContext>>(null);

/** While range end is not chosen yet, returns the allowed chunk that contains `from`. */
function allowedChunkForPendingRangeEnd(
  mode: DatePickerProps['mode'],
  value: Maybe<Date | DateRange>,
  ranges: DatePickerAllowedRange[]
): DatePickerAllowedRange | undefined {
  if (mode !== 'range' || !ranges.length || !value || value instanceof Date) {
    return undefined;
  }
  const { from, to } = value as DateRange;
  if (!from || (to && !isSameDay(from, to))) {
    return undefined;
  }
  const d = startOfDay(from);
  return ranges.find(r => d >= r.from && d <= r.to);
}

/**
 * Widens DayPicker `startMonth`/`endMonth` when the navigation window is narrower
 * than `numberOfMonths`.
 */
export function getCalendarMonthBoundsForRangeDisplay(
  navigationStart: Date | undefined,
  navigationEnd: Date | undefined,
  numberOfMonths: number
): { startMonth?: Date; endMonth?: Date } {
  if (numberOfMonths <= 1) {
    return { startMonth: navigationStart, endMonth: navigationEnd };
  }

  const anchor = navigationStart ?? navigationEnd;
  if (!anchor) {
    return {};
  }

  const startMonth = startOfMonth(navigationStart ?? anchor);
  const endMonth = startOfMonth(navigationEnd ?? anchor);
  const monthSpan =
    navigationStart && navigationEnd
      ? differenceInCalendarMonths(endMonth, startMonth) + 1
      : 1;

  return {
    startMonth,
    endMonth: addMonths(endMonth, Math.max(0, numberOfMonths - monthSpan))
  };
}

/** Disabled days are those outside allowed ranges; while picking range end, only that chunk counts. */
function getAllowedDayMatcher(
  mode: DatePickerProps['mode'],
  value: Maybe<Date | DateRange>,
  normalizedRanges: DatePickerAllowedRange[]
): ((day: Date) => boolean) | undefined {
  if (!normalizedRanges.length) {
    return undefined;
  }
  const pendingChunk = allowedChunkForPendingRangeEnd(
    mode,
    value,
    normalizedRanges
  );
  const ranges = pendingChunk ? [pendingChunk] : normalizedRanges;
  return day => !ranges.some(r => day >= r.from && day <= r.to);
}

export function useDatePickerContext() {
  return maybe.getOrThrow(
    useContext(datePickerContext),
    'DatePicker context not found'
  );
}

/**
 * @deprecated Use {@link import('./DateInput').DateInput} for single dates and
 * {@link import('./DateRangeInput').DateRangeInput} for date ranges.
 */
export function DatePicker({
  className,
  placeholder,
  leftSlot,
  value,
  onChange,
  variant,
  size,
  mode,
  granularity = 'day',
  allowChangeGranularity = true,
  onIncludeHoursChange,
  disabled,
  readOnly,
  popoverExtra,
  align = 'start',
  side = 'bottom',
  maxDate,
  minDate,
  allowedRanges,
  modifiers,
  clearable = false,
  ...rest
}: DatePickerProps) {
  const { t } = useTranslation('ds');
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  useLayoutEffect(() => {
    setInternalValue(value);
  }, [value]);

  const popoverContentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);
  const focusedBeforeOpenRef = useRef<HTMLElement | null>(null);

  function handleOpen() {
    if (readOnly || disabled) {
      return;
    }
    const focusedElement = document.activeElement;
    if (focusedElement instanceof HTMLElement) {
      if (triggerRef.current?.contains(focusedElement)) {
        focusedBeforeOpenRef.current = focusedElement;
      } else {
        focusedBeforeOpenRef.current = null;
      }
    }
    setOpen(true);
  }

  function handleClose() {
    const element = triggerRef.current?.firstElementChild;
    if (element instanceof HTMLElement) {
      setTimeout(() => {
        setOpen(false);

        setTimeout(() => {
          if (popoverContentRef.current?.contains(document.activeElement)) {
            // we focus back only if the focus is still within the popover
            (focusedBeforeOpenRef.current ?? element).focus();
          }
        }, 0);
      }, 150); // allow selection transitions to finish to avoid flicker
    }
  }

  function handleValueChange(
    value: Maybe<Date | DateRange>,
    reason: DatePickerChangeReason
  ) {
    setInternalValue(value);
    onChange(value as any, reason);
  }

  const includeHoursHotkey = 'Comma';
  const inputProps = {
    granularity,
    size,
    className: 'flex-1 h-fit',
    onKeyDown: (e: KeyboardEvent) => {
      if (e.code === 'Backspace' && value) {
        e.preventDefault();
        setInternalValue(null);
        handleClose();
        onChange(null, 'clear');
      }
      if (e.code === includeHoursHotkey) {
        onIncludeHoursChange?.(!includeHours);
      }
      if (e.code === 'Space') {
        // open the calendar
        handleOpen();
        e.preventDefault();
      }
    }
  } as const;

  const includeHours =
    granularity === 'hour' ||
    granularity === 'minute' ||
    granularity === 'second';

  const normalizedAllowedRanges = (allowedRanges ?? []).map(range => ({
    from: startOfDay(range.from),
    to: endOfDay(range.to)
  }));

  const hasAllowedRanges = normalizedAllowedRanges.length > 0;
  const navigationStartMonth =
    minDate ??
    normalizedAllowedRanges
      .map(range => range.from)
      .sort((a, b) => a.getTime() - b.getTime())[0];
  const navigationEndMonth =
    maxDate ??
    normalizedAllowedRanges
      .map(range => range.to)
      .sort((a, b) => b.getTime() - a.getTime())[0];

  const rangeMonthCount = mode === 'range' ? 2 : 1;
  const calendarMonthBounds = getCalendarMonthBoundsForRangeDisplay(
    navigationStartMonth,
    navigationEndMonth,
    rangeMonthCount
  );

  const regularContent = (
    <div className="p-3 flex flex-col h-full gap-2">
      <Calendar
        className="p-0 flex-1"
        required={false}
        autoFocus
        selected={maybe.getOrUndefined(internalValue) as DateRange}
        onSelect={newValue => {
          function fixDate(
            date: Date,
            includeHours: boolean,
            timeSource: Date | undefined
          ) {
            if (!includeHours || !timeSource) return date;

            const fixed = set(date, {
              hours: timeSource.getHours(),
              minutes: timeSource.getMinutes(),
              seconds: timeSource.getSeconds(),
              milliseconds: timeSource.getMilliseconds()
            });
            return fixed;
          }

          // todo: do not reset time if only date is selected
          if (mode === 'range') {
            if (internalValue instanceof Date) {
              // not sure if this is possible case
              setInternalValue(value);
            } else {
              setInternalValue({
                from: fixDate(newValue.from, includeHours, internalValue?.from),
                to: fixDate(newValue.to, includeHours, internalValue?.to)
              });
            }
          } else {
            if (internalValue instanceof Date) {
              const newDate = fixDate(
                newValue,
                includeHours,
                internalValue as Date
              );
              setInternalValue(newDate);
              onChange(newDate, 'close');
            } else {
              // not sure if this is possible case
              setInternalValue(newValue);
              onChange(newValue, 'close');
            }
            handleClose();
          }
        }}
        mode={mode as 'range'}
        excludeDisabled={mode === 'range' && hasAllowedRanges}
        disabled={[
          getAllowedDayMatcher(mode, internalValue, normalizedAllowedRanges),
          minDate && {
            before: minDate
          },
          maxDate && {
            after: maxDate
          }
        ].filter(maybe.isPresent)}
        defaultMonth={
          internalValue instanceof Date ? internalValue : internalValue?.from
        }
        startMonth={calendarMonthBounds.startMonth}
        endMonth={calendarMonthBounds.endMonth}
        numberOfMonths={rangeMonthCount}
        onDayKeyDown={(day, modifiers, e) => {
          if (mode === 'range') {
            if (e.key === 'Enter') {
              handleClose();
              onChange(internalValue as DateRange, 'enter');
              e.preventDefault();
            }
          }
        }}
        modifiers={modifiers}
      />
      <div className="mt-auto flex  flex-wrap gap-2 flex-row justify-between empty:hidden">
        {allowChangeGranularity && (
          <label className="flex items-center gap-2 justify-end">
            <SimpleTooltip
              align="start"
              side="bottom"
              title={
                <Trans
                  t={t}
                  i18nKey="DatePicker.toggleIncludeHoursRich"
                  ns="ds"
                  components={{
                    kbd: <Kbd className="inline-block" />,
                    p: <p />
                  }}
                />
              }
            >
              <div className="flex items-center gap-1 whitespace-nowrap">
                {t('DatePicker.toggleIncludeHours')} <Info className="size-4" />
              </div>
            </SimpleTooltip>
            <Switch
              checked={includeHours}
              onCheckedChange={onIncludeHoursChange}
            />
          </label>
        )}
        {mode === 'range' && (
          <div className="text-fg-muted text-md self-end ml-auto">
            <Trans
              t={t}
              i18nKey="DatePicker.pressEnterToApply"
              ns="ds"
              components={{
                kbd: <Kbd className="bg-bg-subtle inline-flex items-center" />,
                enter: <CornerDownLeft className="size-4" />
              }}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <datePickerContext.Provider
      value={
        {
          value: internalValue,
          onChange: handleValueChange,
          mode,
          onOpenChange: o => {
            if (!o) {
              handleClose();
            } else {
              setInternalValue(value);
            }
          }
        } as DatePickerContext
      }
    >
      <Popover
        open={open}
        onOpenChange={o => {
          if (!o) {
            handleClose();
          } else {
            setInternalValue(value);
          }
        }}
      >
        <PopoverAnchor asChild>
          <InputGroup
            variant={variant}
            size={size}
            className={cn(
              'overflow-hidden justify-between w-fit', // for the border-radius to work
              className
            )}
            ref={triggerRef}
            aria-readonly={readOnly}
            onDoubleClick={e => {
              if (disabled) {
                return;
              }
              if (!open) {
                e.stopPropagation();
                handleOpen();
              }
            }}
            {...rest}
            onCopy={e => {
              // set clipboard with well formatted date
              const valueToCopy =
                mode === 'single'
                  ? value?.toLocaleDateString()
                  : `${value?.from?.toLocaleDateString()} - ${value?.to?.toLocaleDateString()}`;
              if (valueToCopy) {
                e.clipboardData.clearData();
                e.clipboardData.setData('text/plain', valueToCopy);
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {leftSlot}
            <div className="flex flex-row flex-wrap flex-1 gap-x-1 items-center">
              {mode === 'single' ? (
                <DateInput
                  value={value}
                  {...inputProps}
                  data-element="input-group-input"
                  onValueChange={value => {
                    handleValueChange(value, 'input');
                  }}
                  placeholder={placeholder}
                  disabled={disabled}
                  readOnly={readOnly}
                />
              ) : (
                <div
                  data-element="input-group-input"
                  className="flex flex-row flex-1 gap-x-1 items-center flex-wrap"
                >
                  <div className="flex flex-row flex-1 gap-x-1 items-center">
                    <DateInput
                      {...inputProps}
                      value={value?.from}
                      onValueChange={fromValue => {
                        handleValueChange(
                          {
                            ...value,
                            from: maybe.getOrUndefined(fromValue)
                          },
                          'input'
                        );
                      }}
                      placeholder={placeholder}
                      disabled={disabled}
                      readOnly={readOnly}
                    />

                    <ArrowRight className="text-fg-muted mx-auto" />
                  </div>
                  <DateInput
                    {...inputProps}
                    value={value?.to}
                    onValueChange={toDate => {
                      handleValueChange(
                        {
                          from: maybe.getOrElse(
                            value?.from,
                            maybe.getOrUndefined(toDate)
                          ),
                          to: maybe.getOrUndefined(toDate)
                        },
                        'input'
                      );
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                  />
                </div>
              )}
            </div>
            {clearable && value && (
              <InputGroupButton
                onClick={e => {
                  e.stopPropagation();
                  handleValueChange(null, 'clear');
                }}
                tabIndex={-1}
                disabled={disabled}
              >
                <X className="size-4" />
              </InputGroupButton>
            )}
            <PopoverTrigger asChild>
              <InputGroupButton
                aria-expanded={open}
                onClick={handleOpen}
                tabIndex={-1} // use "space" to open the calendar
                disabled={readOnly || disabled}
                aria-readonly={readOnly}
              >
                <CalendarIcon />
              </InputGroupButton>
            </PopoverTrigger>
          </InputGroup>
        </PopoverAnchor>
        <PopoverContent
          align={align}
          side={side}
          className="p-0 space-y-2 max-w-fit min-w-min"
          ref={popoverContentRef}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.code === includeHoursHotkey) {
              onIncludeHoursChange?.(!includeHours);
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onPointerDownOutside={() => {
            mode === 'range' && onChange(internalValue as DateRange, 'close');
          }}
        >
          {popoverExtra ? (
            <div className="flex flex-row gap-0 divide divide-x divide-border-default">
              {popoverExtra}
              <div>{regularContent}</div>
            </div>
          ) : (
            regularContent
          )}
        </PopoverContent>
      </Popover>
    </datePickerContext.Provider>
  );
}
